import {AddressInfo, AmountInfo, BankAccount, ClaimReasonType} from 'supporter';

export interface SelectListItem {
  name: string;
  value: number | string;
}

export interface Shipping {
  requiresShipping: boolean;
  address: AddressInfo;
  shippingNo: number;
  hasAdjustedExchangeDeliveryAmt: boolean;
  divided: boolean;
  deliveryGroupNo: number;
  deliveryType?: string;
  prepaid: boolean;
  deliveryTemplateNo: number;
  combined: boolean;
  originalShippingNo: number;
}

export interface Amounts {
  refundDeliveryAmt: number;
  deliveryAdjustAmt: number;
  returnDelivery?: {
    totalReturnDeliveryAmt?: number;
    totalPureReturnDeliveryAmt?: number;
    prepaidTotalPureReturnDeliveryAmt?: number;
    prepaidProcessedDeliveryAmt?: number;
    returnRemoteDeliveryAmt?: number;
    returnDeliveryAmt?: number;
    prepaidReturnRemoteDeliveryAmt?: number;
    processedDeliveryAmt?: number;
    prepaid?: boolean;
    prepaidTotalReturnDeliveryAmt?: number;
    prepaidReturnDeliveryAmt?: number;
  };
  exchangeDelivery?: {
    prepaidRemoteDeliveryAmt?: number;
    deliveryAmt?: number;
    remoteDeliveryAmt?: number;
    totalDeliveryAmt?: number;
    prepaidDeliveryAmt?: number;
    totalPrepaidDeliveryAmt?: number;
  };
  returnDeliveryAdjustAmt?: number;
  additionalDeliveryAmt?: number;
  before: AmountInfo;
  adjustedAmounts: {
    mainPayAmt: number;
    accumulationPayAmt: number;
    claimAmt: number;
    additionalPayAmt: number;
    payAmt: number;
  };
  productAdjustAmt?: number;
  after: AmountInfo;
  additionalPayAmt?: number;
  exchangeDeliveryAdjustAmt: number;
  refund: AmountInfo;
}

export interface ReturnInfoForm {
  deliveryCompanyType: string;
  invoiceNo: number;
  evadesReturnProcess: boolean;
}

export interface CancelPopupForm {
  responsibleObjectType: ResponsibleObjectType;
  refundType: RefundType;
  reasonDetail: string;
  sellerPaysClaimedDelivery: boolean; // 판매자 부담 여부
  reasonType: ReasonType;
  refundBankAccount: RefundBankAccount;
}

export interface RefundBankAccount {
  depositorName: string;
  bank: string;
  bankName: string;
  account: string;
}

export interface CancelForm {
  responsibleObjectType: ResponsibleObjectType; // BUYER, SELLER
  refundType: RefundType; // PG,ACCOUNT,ZERO_REFUND
  reasonDetail: string;
  sellerPaysClaimedDelivery: boolean; // 판매자 부담 여부
  reasonType: ClaimReasonType;
  refundBankAccount: RefundBankAccount;
}

export interface ReturnForm {
  responsibleObjectType: ResponsibleObjectType; // BUYER, SELLER
  refundType: RefundType; // PG,ACCOUNT,ZERO_REFUND
  reasonDetail: string;
  sellerPaysClaimedDelivery: boolean; // 판매자 부담 여부
  reasonType: ReasonType;
  refundBankAccount: BankAccount;
  returnWayType: ReturnWayType; // SELLER_COLLECT, BUYER_DIRECT_RETURN
  returnDeliveryProposedAmt: number;
  returnDeliveryProposedReason: string;
  delivery: DeliveryForm;
  refundAdjustAmt: string; // comma num string
  refundAdjustReason: string;
}

export interface DeliveryForm {
  invoiceNo: string;
  deliveryCompanyType: string;
  evadesReturnProcess: boolean;
}

//TODO: BPC 쪽 로직 확인하기
/**
 * 취소, 반품 교환의 판매할인적용가 둘 다 사용되는데,
 * 내가 BPC의 반품교환(RETURN_PRODUCT 라는)을 고려했는지 확인할 필요가 있다.
 */
export enum ProductType {
  EXCHANGE_PRODUCT = 'EXCHANGE_PRODUCT',
  CLAIMED_PRODUCT = 'CLAIMED_PRODUCT'
}

export enum ClaimType {
  CANCEL = 'CANCEL',
  EXCHANGE = 'EXCHANGE',
  RETURN = 'RETURN',
  REFUND = 'REFUND'
}

export enum ClaimStatusType {
  CANCEL_NO_REFUND = 'CANCEL_NO_REFUND',
  CANCEL_REQUEST = 'CANCEL_REQUEST',
  CANCEL_PROC_REQUEST_REFUND = 'CANCEL_PROC_REQUEST_REFUND',
  CANCEL_PROC_WAITING_REFUND = 'CANCEL_PROC_WAITING_REFUND',
  CANCEL_DONE = 'CANCEL_DONE',
  RETURN_REQUEST = 'RETURN_REQUEST',
  RETURN_REJECT_REQUEST = 'RETURN_REJECT_REQUEST',
  RETURN_PROC_BEFORE_RECEIVE = 'RETURN_PROC_BEFORE_RECEIVE',
  RETURN_PROC_REQUEST_REFUND = 'RETURN_PROC_REQUEST_REFUND',
  RETURN_PROC_WAITING_REFUND = 'RETURN_PROC_WAITING_REFUND',
  RETURN_REFUND_AMT_ADJUST_REQUESTED = 'RETURN_REFUND_AMT_ADJUST_REQUESTED',
  RETURN_DONE = 'RETURN_DONE',
  EXCHANGE_REQUEST = 'EXCHANGE_REQUEST',
  EXCHANGE_REJECT_REQUEST = 'EXCHANGE_REJECT_REQUEST',
  EXCHANGE_PROC_BEFORE_RECEIVE = 'EXCHANGE_PROC_BEFORE_RECEIVE',
  EXCHANGE_PROC_REQUEST_PAY = 'EXCHANGE_PROC_REQUEST_PAY',
  EXCHANGE_PROC_WAITING = 'EXCHANGE_PROC_WAITING',
  EXCHANGE_PROC_WAITING_PAY = 'EXCHANGE_PROC_WAITING_PAY',
  EXCHANGE_DONE_PAY_DONE = 'EXCHANGE_DONE_PAY_DONE',
  EXCHANGE_PROC_REQUEST_REFUND = 'EXCHANGE_PROC_REQUEST_REFUND',
  EXCHANGE_PROC_WAITING_REFUND = 'EXCHANGE_PROC_WAITING_REFUND',
  EXCHANGE_DONE_REFUND_DONE = 'EXCHANGE_DONE_REFUND_DONE',
  EXCHANGE_DONE = 'EXCHANGE_DONE'
}

export enum ClaimClassType {
  ORDER_CANCEL = 'ORDER_CANCEL', // 주문취소 (전체취소)
  OPTION_CANCEL = 'OPTION_CANCEL', // 옵션취소 (부분취소)
  CANCEL_EXCHANGE = 'CANCEL_EXCHANGE', // 취소교환
  RETURN_EXCHANGE = 'RETURN_EXCHANGE' // 반품교환
}

export enum WithdrawValidationType {
  EXISTS_AFTER_CLAIM_WITH_CART_COUPON = 'EXISTS_AFTER_CLAIM_WITH_CART_COUPON',
  EXISTS_SAME_SHIPPING_NO_CLAIM = 'EXISTS_SAME_SHIPPING_NO_CLAIM',
  WITHDRAWABLE = 'WITHDRAWABLE'
}

export enum ResponsibleObjectType {
  SELLER = 'SELLER',
  BUYER = 'BUYER'
}

export enum RefundType {
  PG = 'PG',
  ACCOUNT = 'ACCOUNT',
  ZERO_REFUND = 'ZERO_REFUND',
  ACCUMULATION = 'ACCUMULATION'
}

export enum ReasonType {
  CHANGE_MIND = 'CHANGE_MIND',
  DEFECTIVE_PRODUCT = 'DEFECTIVE_PRODUCT',
  WRONG_DELIVERY = 'WRONG_DELIVERY',
  OUT_OF_STOCK_SYSTEM = 'OUT_OF_STOCK_SYSTEM',
  CANCEL_BEFORE_PAY = 'CANCEL_BEFORE_PAY',
  WRONG_PRODUCT_DETAIL = 'WRONG_PRODUCT_DETAIL',
  DELAY_DELIVERY = 'DELAY_DELIVERY',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  OTHERS_SELLER = 'OTHERS_SELLER',
  OTHERS_BUYER = 'OTHERS_BUYER'
}

export enum AdditionalPayType {
  CASH = '무통장입금',
  ACCUMULATION = '적립금차감',
  NAVER_PAY = '네이버페이'
}

export enum ReturnWayType {
  SELLER_COLLECT = 'SELLER_COLLECT',
  BUYER_DIRECT_RETURN = 'BUYER_DIRECT_RETURN'
}

export enum PgType {
  DUMMY = 'Dummy',
  PAYCO = 'PAYCO',
  PAYPAL = 'PayPal',
  STRIPE = 'Stripe',
  KCP = 'KCP',
  INICIS = 'INICIS',
  NONE = 'PG없음',
  KCP_MOBILE = 'KCP',
  KCP_APP = 'KCP',
  NAVER_PAY = '네이버페이',
  LIIVMATE = 'LiivMate',
  PAYPALPRO = 'PayPalPro',
  ATHOR_NET = 'AthorizeNet',
  KAKAO_PAY = 'Kakao Pay',
  NAVER_EASY_PAY = '네이버페이 간편결제',
  LG_U_PLUS = '토스페이먼츠'
}
